import NiceModal, { bootstrapDialog, useModal } from "@ebay/nice-modal-react";
import { VizContext } from "../../../models/viz/operations/DataOperation";
import { Button, Form, Modal } from "react-bootstrap";
import dayjs from "dayjs";
import { DateFormats } from "../../../utilities/dates";
import { Entity, FileVideoFrameEntityImage, ThumbnailEntityImage } from "../../../models/viz/Entity";
import { useEffect, useMemo, useState } from "react";
import { capitaliseFirst, tryExtractErrorMessage } from "../../../utilities/helpers";
import { Notifications } from "../../../utilities/Notifications/Notifications";
import {
    createRelatedEntitiesReportPdfDocument,
    createEntitiesTablePdfDocument,
    savePdfFile,
} from "../../../utilities/export/pdf";
import ThumbnailExtractor from "../../../utilities/ThumbnailExtractor";
import { useLazyGetAccessCookiesQuery, useLazyGetStoredFileQuery } from "../../../store/api/kinesense";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import ConditionalSpinner from "../../ConditionalSpinner";
import { TagEventsDataSource } from "../../../models/viz/dataSources/TagEventsDataSource";
import { TagObjectsDataSource } from "../../../models/viz/dataSources/TagObjectsDataSource";
import { EntityProperties } from "../../../models/viz/dataDescriptions/EntityProperties";
import { TagEvent } from "../../../models/tags/TagEvent";

export interface ExportEntitiesToPdfModalProps {
    context: VizContext;
    titleColumnName: string;
    subtitleColumnName: string;
    ignoreEntityProperties?: string[];
    filenamePrefix?: string;
    provideSecondaryListingOption?: boolean;
    secondaryListingLabel?: string;
}

const EntityTargets = ["Filtered", "Selected"] as const;
type EntityTarget = (typeof EntityTargets)[number];
type TagReportMode = "tagObjectsPrimary" | "tagEventsPrimary" | "unsupported";

const ExportEntitiesToPdfModal = NiceModal.create((props: ExportEntitiesToPdfModalProps) => {
    const { view } = props.context;
    const organisationId = useSelector((state: ApplicationState) => state.user?.organisationId);
    const hasSelectedEntities = view?.selectedEntity !== undefined;
    const selectedEntities = hasSelectedEntities ? view.selectedEntities : [];
    const filteredEntities = view?.filteredEntities ?? [];
    const [requestAccessCookies] = useLazyGetAccessCookiesQuery();
    const [requestStoredFile] = useLazyGetStoredFileQuery();

    const date = dayjs().format(DateFormats.yearMonthDay);
    const defaultFilename = !props.filenamePrefix ? date : `${props.filenamePrefix} ${date}`;

    const [entityTarget, setEntityTarget] = useState<EntityTarget>(hasSelectedEntities ? "Selected" : "Filtered");
    const [filename, setFilename] = useState<string | undefined>(defaultFilename);

    const entities = useMemo(() => {
        switch (entityTarget) {
            case "Selected":
                return selectedEntities;
            case "Filtered":
                return filteredEntities;
        }
    }, [entityTarget, filteredEntities, selectedEntities]);

    const [entityImageDataUrlByEntityId, setEntityImageDataUrlByEntityId] = useState<Map<string, string>>(new Map());
    const [previewDataUri, setPreviewDataUri] = useState<string | undefined>(undefined);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(false);
    const [useTagObjectReport, setUseTagObjectReport] = useState<boolean>(false);
    const shouldUseTagObjectReport = (props.provideSecondaryListingOption ?? false) && useTagObjectReport;

    const modal = useModal();

    function resolveTagReportMode(primaryEntities: Entity[]): TagReportMode {
        if (primaryEntities.length === 0) {
            return "tagObjectsPrimary";
        }

        const isTagEventEntity = (entity: Entity): boolean => {
            const tagEvent = entity.sourceObject?.object as Partial<TagEvent> | undefined;
            return Array.isArray(tagEvent?.objectIds) || Array.isArray(tagEvent?.subjectIds);
        };

        const isTagObjectEntity = (entity: Entity): boolean => {
            const sourceObject = entity.sourceObject?.object as { objectId?: unknown } | undefined;
            return sourceObject?.objectId !== undefined;
        };

        const areAllTagEvents = primaryEntities.every(isTagEventEntity);
        const areAllTagObjects = primaryEntities.every(isTagObjectEntity);

        if (areAllTagEvents && !areAllTagObjects) {
            return "tagEventsPrimary";
        }

        if (areAllTagObjects && !areAllTagEvents) {
            return "tagObjectsPrimary";
        }

        return "unsupported";
    }

    async function createTagRelatedReportDocument(primaryEntities: Entity[]) {
        const mode = resolveTagReportMode(primaryEntities);
        if (mode === "unsupported") {
            throw new Error("Tag report can only be generated from all tag object entities or all tag event entities.");
        }

        const projectId = primaryEntities.find((entity) => entity.sourceObject?.projectId !== undefined)?.sourceObject
            ?.projectId;

        if (mode === "tagObjectsPrimary") {
            let secondaryEntities: Entity[] = [];

            if (projectId) {
                const dataSource = new TagEventsDataSource(projectId, props.context.react.dispatch);
                const loadResult = await dataSource.load(false);
                if (loadResult.state === "error") {
                    throw new Error(loadResult.errorMessage);
                }

                const dataDescription = dataSource.getDataDescriptions()[0];
                secondaryEntities = dataSource.getEntities(dataDescription);
            }

            return createRelatedEntitiesReportPdfDocument(primaryEntities, secondaryEntities, {
                primaryOptions: {
                    titleColumnName: props.titleColumnName,
                    subtitleColumnName: props.subtitleColumnName,
                    ignoreEntityProperties: props.ignoreEntityProperties,
                },
                secondaryOptions: {
                    titleColumnName: "Event",
                    subtitleColumnName: "Start Time",
                    ignoreEntityProperties: [
                        EntityProperties.Name.key,
                        EntityProperties.StartTime.key,
                        EntityProperties.EventAction.key,
                    ],
                },
                isRelated: (primaryEntity, secondaryEntity) => {
                    const primaryId = String(primaryEntity.sourceObject?.id ?? "");
                    const tagEvent = secondaryEntity.sourceObject?.object as Partial<TagEvent> | undefined;
                    const relatedIds = [
                        ...(tagEvent?.objectIds ?? []),
                        ...(tagEvent?.subjectIds ?? []),
                        ...(tagEvent?.locationIds ?? []),
                    ].map((id) => String(id));

                    return relatedIds.includes(primaryId);
                },
                getPrimaryTitle: (entity) => entity.getTitle() ?? "",
                getPrimarySubtitle: (entity) => entity.getSubtitle() ?? "",
                emptyRelatedMessage: projectId
                    ? "No related tag events found."
                    : "No project information available for selected tag objects.",
                reportTitle: "Tag Object Report",
            });
        }

        let secondaryEntities: Entity[] = [];

        if (projectId) {
            const dataSource = new TagObjectsDataSource(projectId, props.context.react.dispatch);
            const loadResult = await dataSource.load(false);
            if (loadResult.state === "error") {
                throw new Error(loadResult.errorMessage);
            }

            const dataDescription = dataSource.getDataDescriptions()[0];
            secondaryEntities = dataSource.getEntities(dataDescription);
        }

        return createRelatedEntitiesReportPdfDocument(primaryEntities, secondaryEntities, {
            primaryOptions: {
                titleColumnName: props.titleColumnName,
                subtitleColumnName: props.subtitleColumnName,
                ignoreEntityProperties: props.ignoreEntityProperties,
            },
            secondaryOptions: {
                titleColumnName: "Name",
                subtitleColumnName: "Type",
                ignoreEntityProperties: [EntityProperties.Name.key, EntityProperties.ObjectCategory.key],
            },
            isRelated: (primaryEntity, secondaryEntity) => {
                const objectId = String(secondaryEntity.sourceObject?.id ?? "");
                const tagEvent = primaryEntity.sourceObject?.object as Partial<TagEvent> | undefined;
                const relatedIds = [
                    ...(tagEvent?.objectIds ?? []),
                    ...(tagEvent?.subjectIds ?? []),
                    ...(tagEvent?.locationIds ?? []),
                ].map((id) => String(id));

                return relatedIds.includes(objectId);
            },
            getPrimaryTitle: (entity) => entity.getTitle() ?? "",
            getPrimarySubtitle: (entity) => entity.getSubtitle() ?? "",
            emptyRelatedMessage: projectId
                ? "No related tag objects found."
                : "No project information available for selected tag events.",
            reportTitle: "Tag Event Report",
        });
    }

    useEffect(() => {
        let isCancelled = false;

        const blobToDataUrl = async (blob: Blob): Promise<string> =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

        async function resolveEntityImageDataUrl(entity: Entity): Promise<string | undefined> {
            const firstImage = entity.images?.images?.[0];
            if (firstImage === undefined || !firstImage.hasImage()) {
                return undefined;
            }

            try {
                switch (firstImage.origin) {
                    case "imageFile": {
                        const imageFile = await requestStoredFile({ fileId: firstImage.fileId }).unwrap();
                        const imageUrl = imageFile?.accessUrl?.url;
                        if (!imageUrl) {
                            return undefined;
                        }

                        const response = await fetch(imageUrl);
                        const blob = await response.blob();
                        return await blobToDataUrl(blob);
                    }
                    case "videoFileFrame": {
                        const imageAsVideoFrame = firstImage as FileVideoFrameEntityImage;
                        const videoFile = await requestStoredFile({ fileId: imageAsVideoFrame.fileId }).unwrap();
                        const videoUrl = videoFile?.accessUrl?.url;
                        if (!videoUrl) {
                            return undefined;
                        }

                        const thumbnailUrl = await ThumbnailExtractor.getThumbnail(
                            videoUrl,
                            imageAsVideoFrame.bounds,
                            imageAsVideoFrame.timestamp,
                        );

                        const response = await fetch(thumbnailUrl);
                        const blob = await response.blob();
                        return await blobToDataUrl(blob);
                    }
                    case "thumbnail": {
                        const imageAsThumbnail = firstImage as ThumbnailEntityImage;
                        if (!imageAsThumbnail.fragmentId) {
                            return undefined;
                        }

                        const cookies = await requestAccessCookies({
                            mediaId: imageAsThumbnail.mediaId,
                            target: "frag-image",
                        }).unwrap();

                        if (!cookies || !organisationId) {
                            return undefined;
                        }

                        const domain = `https://${process.env.REACT_APP_KINESENSE_IMAGES_BASE}`;
                        const fileDirectory = `${organisationId}/${imageAsThumbnail.mediaId}/${imageAsThumbnail.runId}/${imageAsThumbnail.fragGroupId}`;
                        const filename = `${imageAsThumbnail.fragmentId}.jpg`;
                        const cookieQuery = `Policy=${cookies["CloudFront-Policy"]}&Key-Pair-Id=${cookies["CloudFront-Key-Pair-Id"]}&Signature=${cookies["CloudFront-Signature"]}`;
                        const requestUrl = `${domain}/${fileDirectory}/${filename}?${cookieQuery}`;

                        const response = await fetch(requestUrl);
                        const blob = await response.blob();
                        return await blobToDataUrl(blob);
                    }
                }
            } catch {
                return undefined;
            }

            return undefined;
        }

        async function updatePreview() {
            setIsGeneratingPreview(true);
            setPreviewDataUri(undefined);

            if (shouldUseTagObjectReport) {
                try {
                    const pdfDocument = await createTagRelatedReportDocument(entities);

                    if (isCancelled) {
                        return;
                    }

                    setEntityImageDataUrlByEntityId(new Map());
                    setPreviewDataUri(pdfDocument.output("datauristring"));
                } finally {
                    if (!isCancelled) {
                        setIsGeneratingPreview(false);
                    }
                }

                return;
            }

            const resolvedMap = new Map<string, string>();

            try {
                for (const entity of entities) {
                    const imageDataUrl = await resolveEntityImageDataUrl(entity);
                    if (imageDataUrl) {
                        resolvedMap.set(entity.id, imageDataUrl);
                    }
                }

                if (isCancelled) {
                    return;
                }

                setEntityImageDataUrlByEntityId(resolvedMap);

                const pdfDocument = createEntitiesTablePdfDocument(entities, {
                    titleColumnName: props.titleColumnName,
                    subtitleColumnName: props.subtitleColumnName,
                    ignoreEntityProperties: props.ignoreEntityProperties,
                    entityImageDataUrlByEntityId: resolvedMap,
                });
                setPreviewDataUri(pdfDocument.output("datauristring"));
            } finally {
                if (!isCancelled) {
                    setIsGeneratingPreview(false);
                }
            }
        }

        updatePreview();

        return () => {
            isCancelled = true;
        };
    }, [
        entities,
        organisationId,
        props.context.react.dispatch,
        requestAccessCookies,
        requestStoredFile,
        shouldUseTagObjectReport,
    ]);

    async function handleOnSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        try {
            if (shouldUseTagObjectReport) {
                const document = await createTagRelatedReportDocument(entities);
                document.save(filename ?? "report.pdf");
            } else {
                savePdfFile(entities, {
                    titleColumnName: props.titleColumnName,
                    subtitleColumnName: props.subtitleColumnName,
                    ignoreEntityProperties: props.ignoreEntityProperties,
                    fileName: filename,
                    entityImageDataUrlByEntityId,
                });
            }

            Notifications.notify(
                "File downloaded successfully",
                `The file "${filename}.pdf" was downloaded successfully`,
                "info",
            );
            modal.hide();
        } catch (e) {
            Notifications.notify(
                "Error while downloading PDF file",
                `The following error was encountered while attempting to download a PDF file: ${tryExtractErrorMessage(
                    e,
                )}`,
                "warning",
            );
        }
    }

    return (
        <Modal {...bootstrapDialog(modal)} centered backdrop="static" keyboard={false} size="xl">
            <Modal.Header closeButton>Export to PDF</Modal.Header>

            <Form onSubmit={handleOnSubmit}>
                <Modal.Body className="gap-2 d-flex flex-column">
                    <Form.Label label="Type">
                        Filename
                        <div className="gap-2 d-flex align-items-center">
                            <Form.Control
                                type="text"
                                value={filename}
                                onChange={(e) => setFilename(e.target.value)}
                                required
                            />
                            .pdf
                        </div>
                    </Form.Label>

                    {hasSelectedEntities && (
                        <Form.Label>
                            Entities to export
                            <Form.Select
                                value={entityTarget}
                                onChange={(e) => setEntityTarget(e.target.value as EntityTarget)}
                                required
                            >
                                {EntityTargets.map((type) => (
                                    <option value={type} key={type}>
                                        {capitaliseFirst(type)} entities
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Label>
                    )}

                    <h5 className="mt-2">Preview</h5>
                    <div className="overflow-auto" style={{ maxHeight: "30rem" }}>
                        <ConditionalSpinner isLoading={isGeneratingPreview} containerClassName="text-center py-3">
                            {previewDataUri && (
                                <object
                                    data={`${previewDataUri}#zoom=100`}
                                    type="application/pdf"
                                    style={{ width: "100%", height: "30rem" }}
                                    aria-label="PDF preview"
                                >
                                    PDF preview is unavailable in this browser.
                                </object>
                            )}
                        </ConditionalSpinner>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    {props.provideSecondaryListingOption && (
                        <Form.Check
                            type="switch"
                            id="tag-object-report-switch"
                            className="me-auto"
                            label={props.secondaryListingLabel ?? "Include related entities"}
                            checked={useTagObjectReport}
                            onChange={(e) => setUseTagObjectReport(e.target.checked)}
                        />
                    )}
                    <Button variant="secondary" onClick={modal.hide}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="success">
                        Download PDF
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
});

export default ExportEntitiesToPdfModal;
